import { useCallback, useEffect, useMemo, useState } from "react";
import { Rocket, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const defaultSettings = {
  enabled: true,
  min_bet: 1,
  max_bet: 20,
  min_cashout_multiplier: 1.1,
  max_cashout_multiplier: 10,
  instant_crash_percent: 12,
  house_edge_percent: 8,
  rules_text: "Bet from your wallet, choose your cashout multiplier, and cash out before the rocket crashes. If it crashes first, the bet is lost.",
};

const inputClass = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-[#2F6FA5]";
const labelClass = "mb-2 block text-xs font-black uppercase tracking-[0.16em] text-[#8FC5F3]";

function clampNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-8 w-14 rounded-full transition-colors ${checked ? "bg-[#2F6FA5]" : "bg-white/10"}`}
      aria-label="Toggle Crash"
    >
      <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${checked ? "translate-x-7" : "translate-x-1"}`} />
    </button>
  );
}

export default function AdminCrash() {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const estimatedRtp = useMemo(() => {
    const edge = Math.max(0, Math.min(50, clampNumber(settings.house_edge_percent, 8)));
    return Math.max(0, 100 - edge);
  }, [settings.house_edge_percent]);

  const sync = useCallback((data) => {
    setSettings({ ...defaultSettings, ...(data?.settings || {}) });
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/crash");
    sync(data);
  }, [sync]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [load]);

  const updateSetting = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        enabled: Boolean(settings.enabled),
        min_bet: clampNumber(settings.min_bet, 1),
        max_bet: clampNumber(settings.max_bet, 20),
        min_cashout_multiplier: clampNumber(settings.min_cashout_multiplier, 1.1),
        max_cashout_multiplier: clampNumber(settings.max_cashout_multiplier, 10),
        instant_crash_percent: clampNumber(settings.instant_crash_percent, 12),
        house_edge_percent: clampNumber(settings.house_edge_percent, 8),
        rules_text: String(settings.rules_text || "").slice(0, 500),
      };
      const { data } = await api.put("/admin/crash", payload);
      sync(data);
      toast.success("Crash settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="px-6 py-10 text-slate-300">Loading Crash settings...</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 text-slate-100 sm:px-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-orange-300">
            <Rocket className="h-5 w-5" />
            <span className="text-xs font-black uppercase tracking-[0.18em]">Wallet game</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black">Crash settings</h1>
          <p className="mt-1 text-sm text-slate-400">Control player wallet bets, cashout range, and house edge.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">House edge</div>
            <div className="text-xl font-black text-orange-300">{Number(settings.house_edge_percent || 0).toFixed(1)}%</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">RTP estimate</div>
            <div className="text-xl font-black text-emerald-300">{estimatedRtp.toFixed(1)}%</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="text-xs text-slate-400">Instant crash</div>
            <div className="text-xl font-black text-red-300">{Number(settings.instant_crash_percent || 0).toFixed(1)}%</div>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5">
        <section className="rounded-3xl border border-white/10 bg-[#111113] p-5">
          <div className="grid gap-4 md:grid-cols-[auto_1fr_1fr] md:items-end">
            <div>
              <span className={labelClass}>Enabled</span>
              <Toggle checked={settings.enabled} onChange={(value) => updateSetting("enabled", value)} />
            </div>
            <label>
              <span className={labelClass}>Minimum bet</span>
              <input className={inputClass} type="number" step="0.01" min="0.01" value={settings.min_bet} onChange={(event) => updateSetting("min_bet", event.target.value)} />
            </label>
            <label>
              <span className={labelClass}>Maximum bet</span>
              <input className={inputClass} type="number" step="0.01" min="0.01" value={settings.max_bet} onChange={(event) => updateSetting("max_bet", event.target.value)} />
            </label>
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-[#111113] p-5">
          <h2 className="mb-4 text-xl font-black">Multiplier rules</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className={labelClass}>Minimum cashout multiplier</span>
              <input className={inputClass} type="number" step="0.01" min="1.01" value={settings.min_cashout_multiplier} onChange={(event) => updateSetting("min_cashout_multiplier", event.target.value)} />
            </label>
            <label>
              <span className={labelClass}>Maximum cashout multiplier</span>
              <input className={inputClass} type="number" step="0.01" min="1.01" value={settings.max_cashout_multiplier} onChange={(event) => updateSetting("max_cashout_multiplier", event.target.value)} />
            </label>
            <label>
              <span className={labelClass}>Instant crash chance %</span>
              <input className={inputClass} type="number" step="0.1" min="0" max="99" value={settings.instant_crash_percent} onChange={(event) => updateSetting("instant_crash_percent", event.target.value)} />
              <p className="mt-2 text-xs text-slate-400">Chance that the rocket crashes at 1.00x.</p>
            </label>
            <label>
              <span className={labelClass}>House edge %</span>
              <input className={inputClass} type="number" step="0.1" min="0" max="50" value={settings.house_edge_percent} onChange={(event) => updateSetting("house_edge_percent", event.target.value)} />
              <p className="mt-2 text-xs text-slate-400">Higher value lowers long-term player return.</p>
            </label>
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-[#111113] p-5">
          <label className="block">
            <span className={labelClass}>Player rules text</span>
            <textarea className={`${inputClass} min-h-[120px]`} value={settings.rules_text} onChange={(event) => updateSetting("rules_text", event.target.value)} />
          </label>
        </section>

        <div className="sticky bottom-4 z-10 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-6 py-3 font-black text-white shadow-lg shadow-black/30 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Crash"}
          </button>
        </div>
      </form>
    </div>
  );
}
